import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Play, Pause, RefreshCw, Copy } from 'lucide-react';
import AdminLayout from '../../components/Layout/AdminLayout';
import StatusPill from '../../components/StatusPill';
import Toast, { useToasts } from '../../components/Layout/Toast';
import MonitorFormModal from './MonitorFormModal';
import { monitorApi } from '../../services/api';
import { formatRelative } from '../../config/appConfig';
import { Monitor, MonitorGroup, Recipient } from '../../types';

const AdminMonitorsPage: React.FC = () => {
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [groups, setGroups] = useState<MonitorGroup[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Monitor | null>(null);
  const [duplicating, setDuplicating] = useState<Monitor | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [intervalFilter, setIntervalFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const toast = useToasts();

  const load = useCallback(async () => {
    try {
      const [m, g, r] = await Promise.all([
        monitorApi.adminGetMonitors(),
        monitorApi.getAdminGroups(),
        monitorApi.getRecipients(),
      ]);
      setMonitors(m.data);
      setGroups(g.data);
      setRecipients(r.data);
    } catch {
      toast.error('Failed to load monitors');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setDuplicating(null); setModalOpen(true); };
  const openEdit = (m: Monitor) => { setEditing(m); setDuplicating(null); setModalOpen(true); };
  const openDuplicate = (m: Monitor) => { setEditing(null); setDuplicating(m); setModalOpen(true); };

  const handleSaved = (msg: string) => { setModalOpen(false); toast.success(msg); load(); };

  const remove = async (m: Monitor) => {
    if (!window.confirm(`Delete monitor "${m.name}"? This removes its checks and incidents.`)) return;
    try { await monitorApi.deleteMonitor(m.id); toast.success('Monitor deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggle = async (m: Monitor) => {
    try { await monitorApi.toggleMonitor(m.id); toast.success(m.active ? 'Monitor paused' : 'Monitor resumed'); load(); }
    catch { toast.error('Toggle failed'); }
  };

  const checkNow = async (m: Monitor) => {
    try { await monitorApi.checkMonitorNow(m.id); toast.success('Check triggered'); load(); }
    catch { toast.error('Check failed'); }
  };

  const availableTags = Array.from(new Set(
    monitors.flatMap(m => (m.tags || '').split(',').map(t => t.trim()).filter(Boolean))
  )).sort((a, b) => a.localeCompare(b));

  const usedIntervals = Array.from(new Set(monitors.map(m => m.interval_seconds))).sort((a, b) => a - b);

  const formatIntervalMinutes = (seconds: number) => {
    const minutes = seconds / 60;
    const display = Number.isInteger(minutes) ? String(minutes) : minutes.toFixed(1).replace(/\.0$/, '');
    return `${display}m`;
  };

  const filteredMonitors = monitors.filter(m => {
    const groupMatches =
      groupFilter === 'all'
        ? true
        : groupFilter === 'ungrouped'
          ? !m.group_id
          : String(m.group_id) === groupFilter;

    const tags = (m.tags || '').split(',').map(t => t.trim()).filter(Boolean);
    const tagMatches = selectedTagFilters.length === 0 ? true : selectedTagFilters.some(tag => tags.includes(tag));
    const intervalMatches = intervalFilter === 'all' ? true : String(m.interval_seconds) === intervalFilter;
    const effectiveStatus = m.active ? m.current_status : 'paused';
    const statusMatches = statusFilter === 'all' ? true : effectiveStatus === statusFilter;
    const q = searchTerm.trim().toLowerCase();
    const searchMatches =
      q.length === 0
        ? true
        : m.name.toLowerCase().includes(q) || m.url.toLowerCase().includes(q);

    return groupMatches && tagMatches && intervalMatches && statusMatches && searchMatches;
  });

  const addTagFilter = (tag: string) => {
    if (!tag || tag === 'all') {
      setSelectedTagFilters([]);
      return;
    }
    setSelectedTagFilters(prev => (prev.includes(tag) ? prev : [...prev, tag]));
  };

  const removeTagFilter = (tag: string) => {
    setSelectedTagFilters(prev => prev.filter(t => t !== tag));
  };

  return (
    <AdminLayout>
      <Toast toasts={toast.toasts} onDismiss={toast.dismiss} />
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Monitors</h1>
            <p className="text-sm text-slate-500 mt-1">Configure and manage endpoint checks.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand-dark">
              <Plus size={16} /> New Monitor
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Monitor name or URL"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Group</label>
              <select
                value={groupFilter}
                onChange={e => setGroupFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All groups</option>
                <option value="ungrouped">Ungrouped</option>
                {groups.map(g => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tag</label>
              <div className="space-y-2">
                <select
                  value=""
                  onChange={e => addTagFilter(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select tag...</option>
                  <option value="all">All tags</option>
                  {availableTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                </select>
                {selectedTagFilters.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTagFilters.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => removeTagFilter(tag)}
                        className="text-[11px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded hover:bg-slate-200"
                        title={`Remove ${tag}`}
                      >
                        {tag} x
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Interval</label>
              <select
                value={intervalFilter}
                onChange={e => setIntervalFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All intervals</option>
                {usedIntervals.map(value => (
                  <option key={value} value={String(value)}>{formatIntervalMinutes(value)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="up">Up</option>
                <option value="down">Down</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : monitors.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No monitors yet. Create your first one.</div>
          ) : filteredMonitors.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No monitors match the selected filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                  <th className="px-5 py-3 font-medium">Monitor</th>
                  <th className="px-5 py-3 font-medium">Group</th>
                  <th className="px-5 py-3 font-medium">Interval</th>
                  <th className="px-5 py-3 font-medium">Tags</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMonitors.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-800">{m.name}</div>
                      <div className="text-xs text-slate-400 truncate max-w-xs"><code>{m.method}</code> {m.url}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-500">{m.group_name || '—'}</td>
                    <td className="px-5 py-3 text-slate-500">{formatIntervalMinutes(m.interval_seconds)}</td>
                    <td className="px-5 py-3 text-slate-600">
                      <div className="flex flex-wrap gap-1.5">
                        {(m.tags || '').split(',').map(t => t.trim()).filter(Boolean).length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          (m.tags || '').split(',').map(t => t.trim()).filter(Boolean).map(tag => (
                            <span key={`${m.id}-${tag}`} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag}</span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={m.active ? m.current_status : 'paused'} size="sm" />
                      <div className="text-[11px] text-slate-400 mt-1">{formatRelative(m.last_checked_at)}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => checkNow(m)} title="Check now" className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded"><RefreshCw size={16} /></button>
                        <button onClick={() => toggle(m)} title={m.active ? 'Pause' : 'Resume'} className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-slate-100 rounded">
                          {m.active ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <button onClick={() => openEdit(m)} title="Edit" className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded"><Pencil size={16} /></button>
                        <button onClick={() => openDuplicate(m)} title="Duplicate" className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded"><Copy size={16} /></button>
                        <button onClick={() => remove(m)} title="Delete" className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <MonitorFormModal
          monitor={editing}
          duplicateFrom={duplicating}
          monitors={monitors}
          groups={groups}
          recipients={recipients}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
          onError={toast.error}
        />
      )}
    </AdminLayout>
  );
};

export default AdminMonitorsPage;
